import type { ExecutionAdapter } from './execution-adapter';
import type {
  ExecuteProcessContext,
  ExecuteProcessResult,
  SimulationNode,
} from '../types';

const toPrimitiveValue = (value: unknown) => {
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (typeof value !== 'string') {
    return value;
  }

  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^(true|false)$/i.test(trimmed)) {
    return trimmed.toLowerCase() === 'true';
  }

  const numeric = Number(trimmed);
  if (!Number.isNaN(numeric) && trimmed !== '') {
    return numeric;
  }

  return trimmed;
};

const formatOutputValue = (value: unknown) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const pickNumericInputs = (inputs: Record<string, unknown>) =>
  Object.values(inputs).filter(
    (item): item is number => typeof item === 'number' && Number.isFinite(item)
  );

const pickStringInputs = (inputs: Record<string, unknown>) =>
  Object.values(inputs)
    .map(item => formatOutputValue(item))
    .filter(Boolean);

const inferProcessComputation = (
  context: ExecuteProcessContext,
  outputNode: SimulationNode,
  index: number
) => {
  const { process, inputs } = context;
  const processKey = `${process.codeName || ''} ${process.label}`.toLowerCase();
  const numericInputs = pickNumericInputs(inputs);
  const stringInputs = pickStringInputs(inputs);
  const sum = numericInputs.reduce((acc, value) => acc + value, 0);
  const avg = numericInputs.length ? sum / numericInputs.length : 0;

  if (numericInputs.length > 0) {
    if (/avg|mean|average|平均/.test(processKey)) return avg;
    if (/count|数量|计数/.test(processKey)) return numericInputs.length;
    if (/min|最小/.test(processKey)) return Math.min(...numericInputs);
    if (/max|最大/.test(processKey)) return Math.max(...numericInputs);
    if (/mul|product|乘/.test(processKey))
      return numericInputs.reduce((acc, value) => acc * value, 1);
    if (/sub|minus|差|减/.test(processKey))
      return numericInputs.slice(1).reduce((acc, value) => acc - value, numericInputs[0]);
    return Number((sum + index).toFixed(2));
  }

  if (outputNode.type === 'state') {
    return outputNode.label || '已触发';
  }

  if (/status|state|状态/.test(`${outputNode.codeName || ''} ${outputNode.label}`)) {
    return `${process.label}完成`;
  }

  if (/flag|enabled|valid|success|ok/.test(processKey)) {
    return true;
  }

  if (stringInputs.length > 0) {
    return `${process.label}: ${stringInputs.join(' / ')}`;
  }

  return `${process.label}结果${index + 1}`;
};

export class MockExecutionAdapter implements ExecutionAdapter {
  async executeProcess(
    context: ExecuteProcessContext
  ): Promise<ExecuteProcessResult> {
    const startedAt = Date.now();
    const outputs: Record<string, unknown> = {};
    const outputNodes = context.process.outputEdges
      .map(edge => context.graph.nodes[edge.targetId])
      .filter(Boolean);

    outputNodes.forEach((outputNode, index) => {
      outputs[outputNode.id] = inferProcessComputation(context, outputNode, index);
    });

    if (outputNodes.length === 0) {
      outputs[context.process.id] = toPrimitiveValue(
        `${context.process.label}已执行`
      );
    }

    return {
      outputs,
      durationMs: Date.now() - startedAt,
    };
  }
}
